import { useRef, type KeyboardEvent, type ReactNode } from 'react'

export interface SegmentedOption<T extends string | number> {
  value: T
  label: ReactNode
}

export interface SegmentedProps<T extends string | number> {
  value: T
  options: SegmentedOption<T>[]
  onChange: (value: T) => void
  /** Accessible name of the group, for example "Show". */
  label: string
  block?: boolean
}

/**
 * A segmented control as a `radiogroup` (Frontend §2.4): one tab stop on the checked option, arrow keys move and
 * select, Home/End jump to the ends.
 */
export function Segmented<T extends string | number>({ value, options, onChange, label, block }: SegmentedProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const checked = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  )

  const move = (to: number) => {
    const option = options[to]
    if (!option) return
    onChange(option.value)
    refs.current[to]?.focus()
  }

  const onKeyDown = (e: KeyboardEvent, i: number) => {
    const n = options.length
    const next: Record<string, number> = {
      ArrowRight: (i + 1) % n,
      ArrowDown: (i + 1) % n,
      ArrowLeft: (i - 1 + n) % n,
      ArrowUp: (i - 1 + n) % n,
      Home: 0,
      End: n - 1,
    }
    const to = next[e.key]
    if (to === undefined) return
    e.preventDefault()
    move(to)
  }

  return (
    <div className={`segmented ${block ? 'block' : ''}`} role="radiogroup" aria-label={label}>
      {options.map((o, i) => (
        <button
          key={String(o.value)}
          ref={(el) => {
            refs.current[i] = el
          }}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={i === checked ? 0 : -1}
          onClick={() => {
            onChange(o.value)
          }}
          onKeyDown={(e) => {
            onKeyDown(e, i)
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
